import { memo, lazy, Suspense } from 'react'

import { clsx } from 'clsx'

import { useTribeClient } from '@tribeplatform/react-sdk'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'
import { useColorSchemeContext } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { ErrorBoundary } from '../common/components/ErrorBoundary/ErrorBoundary.js'
import { linkToFile } from '../common/lib/upload.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import type { BaseEmoji } from '../types/index.js'
import { ImageSizeHelper } from './ImageSizeHelper.js'
import { ImageUpload } from './ImageUpload.js'
import { LinkPicker } from './LinkPicker.js'
import type { ImageSizeConfig, ImagePickerTab } from './types.js'

const Picker = lazy(() =>
  import('../utils/emoji-mart-picker.js').then(m => ({
    default: m.Picker,
  })),
)
const UnsplashPicker = lazy(() =>
  import('./Unsplash/Picker.js').then(m => ({
    default: m.UnsplashPicker,
  })),
)

export type ImagePickerProps = {
  tabs: ImagePickerTab[]
  removable?: boolean
  multiple?: boolean

  sizeConfig?: ImageSizeConfig

  onEmojiChange?: (emoji: BaseEmoji) => void
  onImageChange?: (imageFiles: Array<File>) => void
  onRemove?: () => void
  /** On selecting anything this function will also be called and you can use it to close the popover if you are using this component within a dropdown or modal for example. */
  onSelect?: () => void
  defaultQuery?: string
  fullWidth?: boolean
}

export const ImagePickerTabs = memo(
  ({
    onSelect,
    onEmojiChange,
    onImageChange,
    removable,
    onRemove,
    defaultQuery = '',
    fullWidth = false,
    tabs,
    multiple,
    sizeConfig,
  }: ImagePickerProps) => {
    const { config: tribeClientConfig } = useTribeClient()
    const { unsplashApiKey } = tribeClientConfig || {}
    const intl = useI18n()
    const { colorSchemeMode } = useColorSchemeContext()

    const {
      recommendedWidth,
      recommendedHeight,
      showSizeLimit = true,
    } = sizeConfig ?? {}

    const emojiTab = tabs.includes('emoji') && !!onEmojiChange
    const imageTab = tabs.includes('upload') && !!onImageChange
    const linkTab = tabs.includes('link') && !!onImageChange
    const unsplashTab =
      tabs.includes('unsplash') && !!onImageChange && unsplashApiKey

    if (!emojiTab && !imageTab && !linkTab && !unsplashTab) {
      return null
    }

    const onLinkChange = async (link: string) => {
      const file = await linkToFile(link, intl)
      if (file) {
        onImageChange([file])
        onSelect?.()
      }
    }

    const handleImageChange = files => {
      onImageChange(files)
      onSelect?.()
    }

    const handleEmojiChange = emoji => {
      onEmojiChange(emoji)
      onSelect?.()
    }

    const handleRemove = () => {
      onRemove()
      onSelect?.()
    }

    return (
      <div>
        <Tabs.Group>
          <Tabs.List
            variant="filled"
            className={clsx(!fullWidth && 'p-2 pb-0', onRemove && 'space-s-0')}
            fullWidth
          >
            {emojiTab && (
              <Tabs.Tab name="emoji">
                <T id="Generics.Emoji" defaultMessage="Emoji" />
              </Tabs.Tab>
            )}
            {imageTab && (
              <Tabs.Tab name="upload">
                <T id="Generics.Upload" defaultMessage="Upload" />
              </Tabs.Tab>
            )}
            {linkTab && (
              <Tabs.Tab name="link">
                <T id="Generics.Link.Noun" defaultMessage="Link" />
              </Tabs.Tab>
            )}
            {unsplashTab && <Tabs.Tab name="unsplash">Unsplash</Tabs.Tab>}
            {removable && typeof onRemove === 'function' && (
              <Button
                variant="tertiaryNeutral"
                destructive
                className="shrink-0"
                onClick={handleRemove}
              >
                <T id="Generics.Remove" defaultMessage="Remove" />
              </Button>
            )}
          </Tabs.List>
          <Tabs.Panels>
            {emojiTab && (
              <Tabs.Panel className="-mt-3 w-[352px] h-[435px] max-w-full mx-auto flex justify-center">
                <Suspense fallback={<></>}>
                  <Picker
                    className="border-0 w-full rounded-t-none"
                    theme={colorSchemeMode}
                    skinTonePosition="none"
                    previewPosition="none"
                    onEmojiSelect={handleEmojiChange}
                  />
                </Suspense>
              </Tabs.Panel>
            )}
            {imageTab && (
              <Tabs.Panel
                className={clsx(
                  'max-w-full h-64 mx-auto',
                  !fullWidth && 'w-96 px-2 pb-2',
                )}
              >
                <Suspense fallback={<></>}>
                  <ImageUpload
                    multiple={multiple}
                    helper={
                      <ImageSizeHelper
                        recommendedWidth={recommendedWidth}
                        recommendedHeight={recommendedHeight}
                        showSizeLimit={showSizeLimit}
                      />
                    }
                    onChange={handleImageChange}
                  />
                </Suspense>
              </Tabs.Panel>
            )}
            {linkTab && (
              <Tabs.Panel
                className={clsx(
                  'max-w-full h-64 mx-auto',
                  !fullWidth && 'w-96 px-2 pb-2',
                )}
              >
                <LinkPicker onChange={onLinkChange} />
              </Tabs.Panel>
            )}
            {unsplashTab && (
              <Tabs.Panel
                className={clsx(
                  'max-w-full h-[328px] mx-auto',
                  !fullWidth && 'w-96 px-2 pb-2',
                )}
              >
                <ErrorBoundary>
                  <Suspense fallback={<></>}>
                    <UnsplashPicker
                      onChange={onLinkChange}
                      apiKey={unsplashApiKey}
                      defaultQuery={defaultQuery}
                    />
                  </Suspense>
                </ErrorBoundary>
              </Tabs.Panel>
            )}
          </Tabs.Panels>
        </Tabs.Group>
      </div>
    )
  },
)
